import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Video, X, Loader2, Clock, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useActiveRunsCount } from '../hooks/useActiveRunsCount';
import {
  getActiveRunDisplayMessage,
  getActiveRunDisplayProgress,
  getActiveRunCategoryProgress,
  isActiveRunPreprocessingPhase,
} from '../utils/activeRunsEvents';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Progress } from './ui/progress-1';

interface PreprocessingNotificationProps {
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  autoHide?: boolean;
  refreshInterval?: number;
}

const PreprocessingNotification: React.FC<PreprocessingNotificationProps> = ({ 
  position = 'top-right',
  autoHide = false,
  refreshInterval
}) => {
  const { activeCount, processingCount, pendingCount, activeRun, isLoading } = useActiveRunsCount(refreshInterval);
  const navigate = useNavigate();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  const shouldShow = activeCount > 0 && !isDismissed;

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  const runProgressContext = activeRun ?? {};
  const displayProgress = getActiveRunDisplayProgress(runProgressContext);
  const categoryProgress = getActiveRunCategoryProgress(runProgressContext);
  const isPreprocessing = isActiveRunPreprocessingPhase(runProgressContext);
  const statusMessage = getActiveRunDisplayMessage(runProgressContext, displayProgress);
  const displayPercent = activeRun?.progress != null
    ? displayProgress.percent
    : processingCount > 0
      ? 15
      : 8;

  return (
    <AnimatePresence>
      {shouldShow && (
        <motion.div
          initial={{ opacity: 0, y: position.startsWith('top') ? -50 : 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: position.startsWith('top') ? -50 : 50, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          className={`fixed ${positionClasses[position]} z-50 max-w-sm`}
        >
          <Card className="backdrop-blur-xl bg-gray-900/95 border-cyan-500/30 shadow-2xl shadow-cyan-500/20">
            <CardContent className="p-4">
              {/* Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <Video className="w-5 h-5 text-white" />
                    </div>
                    {isLoading && (
                      <div className="absolute -top-1 -right-1">
                        <Loader2 className="w-4 h-4 text-cyan-400 animate-spin" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">
                      Video Processing
                    </h3>
                    <p className="text-gray-400 text-xs">
                      {activeCount} video{activeCount > 1 ? 's' : ''} in queue
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setIsMinimized(!isMinimized)}
                    className="p-1.5 rounded-lg hover:bg-gray-800/50 transition-colors"
                    aria-label={isMinimized ? "Expand" : "Minimize"}
                  >
                    <motion.div
                      animate={{ rotate: isMinimized ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <svg 
                        className="w-4 h-4 text-gray-400 hover:text-white" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </motion.div>
                  </button>
                  {autoHide && (
                    <button
                      onClick={() => setIsDismissed(true)}
                      className="p-1.5 rounded-lg hover:bg-gray-800/50 transition-colors"
                      aria-label="Dismiss"
                    >
                      <X className="w-4 h-4 text-gray-400 hover:text-white" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <AnimatePresence>
                {!isMinimized && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="space-y-3 overflow-hidden"
                  >
                    {/* Status Badges */}
                    <div className="flex flex-wrap gap-2">
                      {processingCount > 0 && (
                        <Badge 
                          variant="info" 
                          appearance="light" 
                          size="sm"
                          className="flex items-center gap-1.5"
                        >
                          <Loader2 className="w-3 h-3 animate-spin" />
                          <span>{processingCount} Processing</span>
                        </Badge>
                      )}
                      {pendingCount > 0 && (
                        <Badge 
                          variant="warning" 
                          appearance="light" 
                          size="sm"
                          className="flex items-center gap-1.5"
                        >
                          <Clock className="w-3 h-3" />
                          <span>{pendingCount} Queued</span>
                        </Badge>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400 truncate pr-2">
                          {statusMessage}
                        </span>
                        <span className="text-cyan-400 font-medium shrink-0">
                          {displayPercent}%
                        </span>
                      </div>
                      <Progress 
                        value={displayPercent} 
                        className="h-2 bg-gray-800/50"
                        indicatorClassName="bg-gradient-to-r from-cyan-500 to-blue-600"
                      />
                      {categoryProgress && (
                        <p className="text-[11px] text-gray-500 tabular-nums">
                          {categoryProgress.done}/{categoryProgress.total} {categoryProgress.label}
                        </p>
                      )}
                      {isPreprocessing && (
                        <p className="text-[11px] text-gray-500">
                          Compressing video for faster AI analysis…
                        </p>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex items-center space-x-2 text-xs text-gray-400 bg-gray-800/30 rounded-lg p-2">
                      <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <p>
                        You'll be notified when processing completes. Estimated time: 2-5 minutes per video.
                      </p>
                    </div>

                    {/* View Details Link */}
                    <button
                      onClick={() => navigate('/status', {
                        state: {
                          focusRequestId: activeRun?.requestId,
                          fromQueueNotification: true
                        }
                      })}
                      className="w-full text-center text-xs text-cyan-400 hover:text-cyan-300 font-medium transition-colors py-2 rounded-lg hover:bg-cyan-500/10"
                    >
                      View detailed status →
                    </button>
                    {activeRun?.requestId && (
                      <p className="text-[11px] text-gray-500 text-center">
                        Showing: {activeRun.videoName || `Request ${activeRun.requestId.slice(0, 8)}...`}
                      </p>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PreprocessingNotification;
